/**
 * Growth Trends Chart Component
 * Line chart showing companies and users growth over months
 */
import React from 'react';
import { Card } from 'antd';
import { Line } from '@ant-design/plots';
import { CompanyGrowthData } from '../../services/superAdminService';
import './GrowthTrendsChart.css';

interface GrowthTrendsChartProps {
  data?: CompanyGrowthData[];
  loading?: boolean;
}

const GrowthTrendsChart: React.FC<GrowthTrendsChartProps> = ({ data, loading = false }) => {
  if (loading || !data || data.length === 0) {
    return (
      <Card className="growth-trends-chart-card" loading={true}>
        <div className="chart-title">Growth Trends</div>
      </Card>
    );
  }

  // Transform data for the chart
  const chartData = data.flatMap((d) => [
    { month: d.month, type: 'New Companies', value: d.new_companies },
    { month: d.month, type: 'Total Companies', value: d.total_companies },
  ]);

  const config = {
    data: chartData,
    xField: 'month',
    yField: 'value',
    seriesField: 'type',
    smooth: true,
    point: {
      size: 4,
      shape: 'circle',
    },
    legend: {
      position: 'bottom' as const,
      itemName: {
        formatter: (text: string) => {
          return `→ ${text}`;
        },
      },
    },
    color: ['#2196f3', '#9c27b0'],
    xAxis: {
      label: {
        style: {
          fill: '#666',
        },
      },
      grid: {
        line: {
          style: {
            stroke: '#e0e0e0',
            lineDash: [4, 4],
          },
        },
      },
    },
    yAxis: {
      label: {
        style: {
          fill: '#666',
        },
      },
      grid: {
        line: {
          style: {
            stroke: '#e0e0e0',
            lineDash: [4, 4],
          },
        },
      },
    },
    tooltip: {
      customContent: (title: string, items: any[]) => {
        if (!items || items.length === 0) return '';
        return `
          <div style="padding: 8px; background: white; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15);">
            <div style="font-weight: 600; margin-bottom: 8px;">${title}</div>
            ${items.map((item: any) => `
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${item.color};"></span>
                <span style="color: ${item.color};">${item.name} : ${item.value}</span>
              </div>
            `).join('')}
          </div>
        `;
      },
    },
    area: {
      style: {
        fillOpacity: 0.3,
      },
    },
  };

  return (
    <Card className="growth-trends-chart-card">
      <div className="chart-title">Growth Trends</div>
      <Line {...config} />
    </Card>
  );
};

export default GrowthTrendsChart;
